/**
 * SDK 业务埋点(即时上报)
 *
 * 镜像主应用 @/lib/statistic/statistic 的实体组装与上报,但适配 SDK 独立 bundle:
 * - 不依赖 zustand(SDK bundle 禁入 useUserStore/useHotelStore)
 * - userID 每次 point() 实时读 shared-storage.readUserStorage
 *   (主应用单例构造时读一次,登录晚于构造则恒为 0;SDK 侧修正为每次实时读)
 * - FromKey(酒店ID)读 readHotelCache(同源共享缓存;BFF 另服务端推导 hotelId 字段,
 *   但 FromKey 需客户端填,见 endpoints.ts statisticEventlog 注释)
 * - device/session id 复用主应用 storage key(_sessionid/_deviceid),同源共享,
 *   SDK 埋点与主应用埋点 device/session 对齐,联合分析无断层
 * - raw fetch 同源 BFF /api/statistic/eventlog(不走 client.ts:埋点 auth:none,
 *   避免 401 触发 onUnauthorized 弹登录窗);keepalive:true 作离开兜底;
 *   best-effort 吞错,不阻塞业务
 *
 * 即时上报(非批量):每次 point() 一个请求,对齐主应用 Statistic.point;受
 * SDKConfig.analytics 开关控制(默认开)。
 */
import { getConfig } from '../config';
import { readUserStorage, readHotelCache } from '@/lib/shared-storage';
import { getDomain } from '../api/hotel';
import { os as getOS, app as getApp } from '@/lib/statistic/ua';
import type { StatisticEntity, PointData } from '@/lib/statistic/types';

const isBrowser = typeof window !== 'undefined';

/** 与主应用同 key,同源共享 session id */
const SESSION_KEY = '_sessionid';
/** 与主应用同 key,同源共享 device id */
const DEVICE_KEY = '_deviceid';

/** 生成 32 位 GUID(与主应用 newguid 一致) */
function newguid(): string {
  let guid = '';
  for (let i = 1; i <= 32; i++) {
    const n = Math.floor(Math.random() * 16.0).toString(16);
    guid += n;
    if (i === 8 || i === 12 || i === 16 || i === 20) guid += '-';
  }
  return guid;
}

/** 会话 id(sessionStorage,无则生成;与主应用同 key) */
function sessionid(): string {
  let id = sessionStorage.getItem(SESSION_KEY);
  if (id) return id;
  id = newguid();
  sessionStorage.setItem(SESSION_KEY, id);
  return id;
}

/** 设备 id(localStorage,无则生成;与主应用同 key) */
function deviceid(): string {
  let id = localStorage.getItem(DEVICE_KEY);
  if (id) return id;
  id = newguid();
  localStorage.setItem(DEVICE_KEY, id);
  return id;
}

/** 组装埋点实体(镜像主应用 Statistic 构造 + handleRequestData 的字段填充) */
function assembleEntity(data: PointData): StatisticEntity {
  const entity: StatisticEntity = {
    Url: '',
    RefererUrl: '',
    PageTitle: '',
    SessionId: '',
    IP: '',
    Browser: '',
    DeviceHeight: 0,
    DeviceWidth: 0,
    DeviceId: '',
    AppVer: '',
    AppVerNum: 0,
    OS: '',
    APP: '',
    Language: '',
    UserID: 0,
    CID: 0,
    Data: '',
    Page: '',
    Action: '',
    ActionValue: '',
    SourceKey: '',
    Element: '',
    FromKey: '',
  };

  // 设备/会话/浏览器上下文(仅客户端采集)
  if (isBrowser) {
    entity.Url = window.location.href;
    entity.RefererUrl = document.referrer;
    entity.PageTitle = document.title;
    entity.SessionId = sessionid();
    entity.Browser = window.navigator.userAgent;
    entity.DeviceHeight = window.screen.height;
    entity.DeviceWidth = window.screen.width;
    entity.DeviceId = deviceid();
    entity.OS = getOS();
    entity.APP = getApp();
    entity.Language = (
      window.navigator.language ||
      (window.navigator as unknown as { browserLanguage?: string }).browserLanguage ||
      ''
    ).toLowerCase();
  }

  // 业务字段
  entity.Page = data.page ?? '';
  entity.Action = data.action;
  entity.ActionValue = data.actionValue ?? '';
  entity.Element = data.element ?? '';
  entity.SourceKey = data.sourceKey ?? '';

  // userID:优先入参 uid,否则实时读登录态(修主应用单例读一次的缺陷)
  const loggedInUid = readUserStorage()?.user?.userID ?? 0;
  entity.UserID = data.uid ?? loggedInUid ?? 0;

  // FromKey:酒店ID(同源 readHotelCache 缓存;BFF 另推导 hotelId 字段,FromKey 需客户端填)
  const hotel = readHotelCache(getDomain());
  entity.FromKey = hotel?.id != null ? String(hotel.id) : '';

  // 曝光事件不记录浏览器与来源(与主应用一致)
  if (data.action === '曝光') {
    entity.Browser = '';
    entity.RefererUrl = '';
  }

  return entity;
}

/**
 * 主埋点方法(即时上报,best-effort)。
 * @param data 见 PointData;page 由调用方传入(如「酒店官网首页-订房模块」)
 */
export function point(data: PointData): void {
  if (!isBrowser) return;
  if (!getConfig().analytics) return;

  const entity = assembleEntity(data);

  // raw fetch 同源 BFF(不走 client.ts,避免 401 触发登录弹窗);keepalive 离开兜底
  const { basePath } = getConfig();
  const origin = typeof location !== 'undefined' ? location.origin : 'http://localhost';
  const url = new URL(`${basePath}/api/statistic/eventlog`, origin);
  // 透传 domain(dev/test 注入 x-hotel-domain;生产走子域名 host 头),与 client.ts 一致
  const domain = getDomain();
  if (domain) url.searchParams.set('domain', domain);

  try {
    void fetch(url.toString(), {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(entity),
      keepalive: true,
    }).catch(() => {
      /* best-effort:吞错,不影响业务 */
    });
  } catch {
    /* 吞错 */
  }
}
